import React, { useState, useEffect } from 'react';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18next from 'core/i18n';

import { Button, DialogContent, Input, palettes, Theme, useFlashMessage } from '@indigo-ag/component-module';
import { Credit, ShareholderCompany, ShareholderPartner } from '@indigo-ag/schema';

import { createStylesHook, EnhancedDialog, utils, ModalActionButtons, numbers } from 'shared';
import { ParticipationProps } from '../../Participation';

export interface ParticipationModalProps extends ParticipationProps {
  closeModal: () => void;
  open: boolean;
  // TODO create type for function
  updateCredit: (newCreditInfo: Partial<Credit>) => Promise<unknown>;
}

const useStyles = createStylesHook((theme: Theme) => ({
  flexDocument: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  inputStyle: {
    marginRight: '32px !important'
  },
  size14Black: {
    fontSize: theme.typography.pxToRem(14),
    color: palettes.neutral['700']
  },
  textModalTitle: {
    color: palettes.neutral['900'],
    fontSize: theme.typography.pxToRem(14),
    marginBottom: 25,
    fontWeight: 'bold'
  }
}));

const initialNewPartner: ShareholderPartner = {
  fullName: '',
  CNPJ: '',
  position: '',
  participation: 0
};

const initialNewCompany: ShareholderCompany = {
  CNPJ: '',
  name: '',
  participation: 0
};

export const ParticipationModal: React.FunctionComponent<ParticipationModalProps> = ({
  closeModal,
  open,
  participationInfo,
  updateCredit
}) => {
  /**
   * State
   */
  const [newPartner, setNewPartner] = useState<ShareholderPartner>(initialNewPartner);
  const [newCompany, setNewCompany] = useState<ShareholderCompany>(initialNewCompany);
  const [shareHolders, setShareHolders] = useState<ShareholderPartner[]>(participationInfo?.shareholderPartner || []);
  const [companiesShares, setCompaniesShares] = useState<ShareholderCompany[]>(
    participationInfo?.shareholderCompany || []
  );

  const getDefaultState = (): void => {
    setNewPartner(initialNewPartner);
    setNewCompany(initialNewCompany);
    setShareHolders(participationInfo?.shareholderPartner || []);
    setCompaniesShares(participationInfo?.shareholderCompany || []);
  };

  useEffect(() => {
    getDefaultState();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Styles
   */
  const classes = useStyles();

  const isNewPartnerCnpjValid = newPartner.CNPJ?.length === 14;
  const isNewCompanyCnpjValid = newCompany.CNPJ.length === 14;

  const isAddShareBtnDisabled = !newPartner.fullName || !newPartner.position || !newPartner.participation;
  const isAddCompanyShareDisabled = !newCompany.name || !newCompany.participation;

  const { addFlashMessage } = useFlashMessage();

  const updateInfo = () => {
    const newShareHolders = [...shareHolders];
    const newCompaniesShares = [...companiesShares];

    if (!isAddShareBtnDisabled) {
      newShareHolders.push(newPartner);
    }

    if (!isAddCompanyShareDisabled) {
      newCompaniesShares.push(newCompany);
    }

    return updateCredit({
      shareholderPartner: utils.graphql.removeTypename(newShareHolders),
      shareholderCompany: utils.graphql.removeTypename(newCompaniesShares)
    }).then(() => {
      addFlashMessage({
        content: `${i18next.t('shareHolding')} ${i18next.t('hasBeenUpdatedSuccessfully')}`,
        title: '',
        variant: 'success'
      });
    });
  };

  const updateNewPartnerInfo = (key: keyof ShareholderPartner) => {
    return (event: React.ChangeEvent<HTMLInputElement>): void => {
      switch (key) {
        case 'participation':
          return setNewPartner({ ...newPartner, [key]: Number(event.target.value) });
        case 'CNPJ':
          return setNewPartner({
            ...newPartner,
            [key]:
              numbers.test(event.target.value) || event.target.value.length === 0 ? event.target.value : newPartner.CNPJ
          });
        default:
          return setNewPartner({ ...newPartner, [key]: event.target.value });
      }
    };
  };

  const updateNewCompanyInfo = (key: keyof ShareholderCompany) => {
    return (event: React.ChangeEvent<HTMLInputElement>): void => {
      switch (key) {
        case 'participation':
          return setNewCompany({ ...newCompany, [key]: Number(event.target.value) });
        case 'CNPJ':
          return setNewCompany({
            ...newCompany,
            [key]:
              numbers.test(event.target.value) || event.target.value.length === 0 ? event.target.value : newCompany.CNPJ
          });
        default:
          return setNewCompany({ ...newCompany, [key]: event.target.value });
      }
    };
  };

  const addShareHolder = (): void => {
    setShareHolders([...shareHolders, newPartner]);
    setNewPartner(initialNewPartner);
  };

  const addCompanyShare = (): void => {
    setCompaniesShares([...companiesShares, newCompany]);
    setNewCompany(initialNewCompany);
  };

  const handleRemoveShareHolder = (holder: number) => {
    return () => {
      const newShareHolderList = shareHolders.filter((_, index) => index !== holder);
      setShareHolders(newShareHolderList);
    };
  };

  const handleRemoveCompany = (company: number) => {
    return () => {
      const newCompaniesList = companiesShares.filter((_, index) => index !== company);
      setCompaniesShares(newCompaniesList);
    };
  };

  const shouldSaveBtnBeDisabled =
    isAddShareBtnDisabled && shareHolders.length === 0 && isAddCompanyShareDisabled && companiesShares.length === 0;

  return (
    <EnhancedDialog open={open} onClose={closeModal} title={i18next.t('shareHolding')}>
      <DialogContent>
        <div style={{ marginBottom: 32 }}>
          <p className={classes.textModalTitle}>{i18next.t('shareHolderPartner')}</p>
          <Input
            label={i18next.t('fullName')}
            value={newPartner.fullName}
            onChange={updateNewPartnerInfo('fullName')}
          />
          <Input
            label="CNPJ"
            value={newPartner.CNPJ || ''}
            onChange={updateNewPartnerInfo('CNPJ')}
            variant="text"
            error={!isNewPartnerCnpjValid && newPartner.CNPJ?.length ? i18next.t('cnpjWarning') : ''}
          />
          <div className={classes.flexDocument}>
            <Input
              label={i18next.t('position')}
              className={classes.inputStyle}
              value={newPartner.position}
              variant="text"
              onChange={updateNewPartnerInfo('position')}
            />
            <Input
              label={i18next.t('participation')}
              units="%"
              value={isNaN(newPartner.participation) ? '' : String(newPartner.participation)}
              onChange={updateNewPartnerInfo('participation')}
            />
          </div>
          <Button
            size="small"
            variant="secondary"
            disabled={isAddShareBtnDisabled || (Boolean(newPartner.CNPJ?.length) && !isNewPartnerCnpjValid)}
            onClick={addShareHolder}
          >
            {i18next.t('add')}
          </Button>
        </div>

        {shareHolders.length > 0 && <p className={classes.size14Black}>{i18next.t('shareHolderPartner')}</p>}
        {shareHolders &&
          shareHolders.map((holder, index: number) => (
            <div key={index} className={classes.flexDocument} style={{ marginBottom: 20 }}>
              <span style={{ marginBottom: 10 }}>
                <FontAwesomeIcon
                  icon={faCheck}
                  size="lg"
                  style={{ color: palettes.coastal.core.mediumBlue, marginRight: 20 }}
                />
                {` ${holder.fullName} - ${holder.position} - ${holder.participation}%`}
              </span>
              <span
                onClick={handleRemoveShareHolder(index)}
                style={{ color: palettes.coastal.core.mediumBlue, cursor: 'pointer' }}
              >
                {i18next.t('remove')}
              </span>
            </div>
          ))}

        <div style={{ marginBottom: 32 }}>
          <p className={classes.textModalTitle}>{i18next.t('companyShares')}</p>
          <Input
            label={i18next.t('company')}
            value={newCompany.name}
            onChange={updateNewCompanyInfo('name')}
            variant="text"
          />
          <Input
            label="CNPJ"
            value={newCompany.CNPJ || ''}
            onChange={updateNewCompanyInfo('CNPJ')}
            variant="text"
            error={!isNewCompanyCnpjValid && newCompany.CNPJ.length ? i18next.t('cnpjWarning') : ''}
          />
          <div className={classes.flexDocument}>
            <Input
              label={i18next.t('participation')}
              units="%"
              value={isNaN(newCompany.participation) ? '' : String(newCompany.participation)}
              onChange={updateNewCompanyInfo('participation')}
            />
          </div>
          <Button
            size="small"
            variant="secondary"
            disabled={isAddCompanyShareDisabled || (Boolean(newCompany.CNPJ.length) && !isNewCompanyCnpjValid)}
            onClick={addCompanyShare}
          >
            {i18next.t('add')}
          </Button>
        </div>

        {companiesShares.length > 0 && <p className={classes.size14Black}>{i18next.t('companyShares')}</p>}
        {companiesShares &&
          companiesShares.map((company, index: number) => (
            <div key={index} className={classes.flexDocument} style={{ marginBottom: 20 }}>
              <span style={{ marginBottom: 10 }}>
                <FontAwesomeIcon
                  icon={faCheck}
                  size="lg"
                  style={{ color: palettes.coastal.core.mediumBlue, marginRight: 20 }}
                />
                {` ${company.name} - ${company.participation}%`}
              </span>
              <span
                onClick={handleRemoveCompany(index)}
                style={{ color: palettes.coastal.core.mediumBlue, cursor: 'pointer' }}
              >
                {i18next.t('remove')}
              </span>
            </div>
          ))}
      </DialogContent>
      <ModalActionButtons
        changeButtonName={i18next.t('saveChanges')}
        closeModal={closeModal}
        disabled={shouldSaveBtnBeDisabled}
        save={updateInfo}
      />
    </EnhancedDialog>
  );
};
